import { useQueryParams } from "@rentiohq/shared-frontend/dist/hooks/useQueryParams";
import * as brokerHooks from "@rentiohq/shared-frontend/dist/redux/broker/broker.hooks";
import * as contactSelectors from "@rentiohq/shared-frontend/dist/redux/contact/contact.selectors";
import * as paymentActionsV1 from "@rentiohq/shared-frontend/dist/redux/payment/payment.actions";
import * as paymentSelectorsV1 from "@rentiohq/shared-frontend/dist/redux/payment/payment.selectors";
import * as rentDepositActions from "@rentiohq/shared-frontend/dist/redux/rentDeposit/rentDeposit.actions";
import * as rentDepositSelectors from "@rentiohq/shared-frontend/dist/redux/rentDeposit/rentDeposit.selectors";
import * as contractActions from "@rentiohq/shared-frontend/dist/reduxV2/contract/contract.actions";
import * as paymentOrderActionsV2 from "@rentiohq/shared-frontend/dist/reduxV2/paymentOrder/paymentOrder.actions";
import * as paymentOrderSelectorsV2 from "@rentiohq/shared-frontend/dist/reduxV2/paymentOrder/paymentOrder.selectors";
import * as paymentOrderUtils from "@rentiohq/shared-frontend/dist/reduxV2/paymentOrder/paymentOrder.utils";
import { IExtraChecklistItem } from "@rentiohq/shared-frontend/dist/reduxV2/preferences";
import { EBrokerFeature } from "@rentiohq/shared-frontend/dist/types/broker.types";
import { IContact } from "@rentiohq/shared-frontend/dist/types/contact.types";
import { IContract } from "@rentiohq/shared-frontend/dist/types/contract.types";
import { EPreferences } from "@rentiohq/shared-frontend/dist/types/preferences.types";
import { IProperty } from "@rentiohq/shared-frontend/dist/types/property.types";
import { ERentDepositStatus } from "@rentiohq/shared-frontend/dist/types/rentDeposit.types";
import { Card, ESpacings } from "@rentiohq/web-shared/dist/components";
import { ELocationDescriptionType } from "forms/orderLocationDescription/schema.orderLocationDescription.types";
import { compact } from "lodash";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { IRootStore } from "redux/reducers";
import { NumberParam, StringParam } from "serialize-query-params";
import { EnergyWaterExitModal } from "../../../../../../../../components/EnergyWaterExitModal";
import { LocationDescriptionModal } from "../../../../../../../../components/LocationDescriptionModal";
import { AdministrationItems } from "../AdministrationItems";
import { useGetAdministrationExitItems } from "../AdministrationItems/useGetAdministrationItems";
import { EActionStatus, IChecklistItem } from "../ChecklistItem";
import { ChecklistItems } from "../ChecklistItems";
import { useGetExitChecklistItems } from "../ChecklistItems/useGetExitChecklistItems";

interface IProps {
  contract: IContract;
  property: IProperty;
  showChecklistItems: boolean;
  setShowChecklistItems: (show: boolean) => void;
  showCircles?: boolean;
}

export const ExitAdministration = ({
  contract,
  property,
  showChecklistItems,
  setShowChecklistItems,
  showCircles,
}: IProps) => {
  const shouldFetchData = showCircles || showChecklistItems;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const filter = {
    and: [{ contractId: { eq: contract.id } }, { completedAt: { is: null } }],
  };

  // const hasRentioMoveForBroker = brokerHooks.useBrokerFeature(
  //   EBrokerFeature.RentioMove,
  // );

  const hasPaymentV2ForBroker = brokerHooks.useBrokerFeature(
    EBrokerFeature.PaymentV2,
  );

  const [_, setQueryParamValue] = useQueryParams({
    documentId: StringParam,
    rentDepositId: NumberParam,
    moveRequestId: NumberParam,
  });

  // Local state
  const [showLocationDescriptionModal, setShowLocationDescriptionModal] =
    React.useState(false);
  const [showRequestRentioMoveService, setShowRequestRentioMoveService] =
    React.useState(false);

  // Extract contacts from contract members
  const contractContacts = useSelector((state: IRootStore) => {
    const accountIds = contract.members?.map(member => member.account.id) || [];
    const contacts = contactSelectors.getContactsByIds(state, accountIds);
    return (
      contacts?.filter(
        (contact): contact is IContact => contact !== undefined,
      ) || []
    );
  });

  // Rent deposits
  const rentDepositsIdentifier = `rent-deposits-contract-${contract.id}`;

  const isFetchingRentDeposits = useSelector((state: IRootStore) =>
    rentDepositSelectors.isFetchingRentDeposits(state, rentDepositsIdentifier),
  );

  const rentDeposits = useSelector((state: IRootStore) =>
    rentDepositSelectors.getRentDeposits(state, rentDepositsIdentifier),
  );

  // Payment orders V1
  const paymentOrdersIdentifier = `contracts-payment-orders-count-${contract.id}`;

  const paymentOrdersV1 = useSelector((state: IRootStore) =>
    paymentSelectorsV1.getPaymentOrdersByIdentifier(
      state,
      paymentOrdersIdentifier,
    ),
  );

  const isFetchingPaymentOrdersV1 = useSelector((state: IRootStore) =>
    paymentSelectorsV1.isFetchingPaymentOrdersByIdentifier(
      state,
      paymentOrdersIdentifier,
    ),
  );

  // Payment orders V2
  const paymentOrdersV2 = useSelector(
    (state: IRootStore) =>
      paymentOrderSelectorsV2.getPaged(
        state,
        paymentOrderUtils.getPaymentOrdersQuery({
          filter,
        }),
      )?.items,
  );

  const isFetchingPaymentOrdersV2 = useSelector(
    (state: IRootStore) =>
      paymentOrderSelectorsV2.getPaged(
        state,
        paymentOrderUtils.getPaymentOrdersQuery({
          filter,
        }),
      )?.isFetching,
  );

  //   const hasRentioMoveServiceForBroker = brokerHooks.useBrokerFeature(
  //     EBrokerFeature.RentioMove,
  //   );

  // Actions
  // Rent deposits
  React.useEffect(() => {
    if (!shouldFetchData) {
      return;
    }

    if (contract.manuallyCompletedEndRentDeposit) {
      return;
    }

    if (isFetchingRentDeposits) {
      return;
    }

    dispatch(
      rentDepositActions.getRentDeposits.actions.start({
        identifier: rentDepositsIdentifier,
        refetch: true,
        extraFilterData: {
          where: {
            basicContractId: contract.id,
            status: { neq: ERentDepositStatus.Cancelled },
          },
          order: "createdAt DESC",
        },
        limit: 1,
      }),
    );
  }, [contract.id, shouldFetchData]);

  // Payment orders V2
  React.useEffect(() => {
    if (!shouldFetchData) {
      return;
    }

    if (!hasPaymentV2ForBroker) {
      return;
    }

    if (contract.manuallyCompletedEndPaymentRent) {
      return;
    }

    if (isFetchingPaymentOrdersV2) {
      return;
    }

    dispatch(
      paymentOrderActionsV2.getPagedStart.getAction({
        query: paymentOrderUtils.getPaymentOrdersQuery({
          filter,
        }),
      }),
    );
  }, [contract.id, shouldFetchData]);

  // Payment orders V1
  React.useEffect(() => {
    if (!shouldFetchData) {
      return;
    }

    if (hasPaymentV2ForBroker) {
      return;
    }

    if (contract.manuallyCompletedEndPaymentRent) {
      return;
    }

    if (isFetchingPaymentOrdersV1) {
      return;
    }

    dispatch(
      paymentActionsV1.getPaymentOrdersByIdentifier.actions.start({
        paymentOrdersIdentifier,
        refetch: true,
        filterData: {
          where: { contractId: contract.id, completedAt: null },
        },
      }),
    );
  }, [contract.id, shouldFetchData]);

  const exitExtraActions = React.useMemo(() => {
    const exit = contract.extraChecklist?.find(
      e => e.key === EPreferences.ChecklistExtraExit,
    );
    if (!exit || exit.actions.length === 0) return undefined;
    return [...exit.actions].sort((a, b) => a.index - b.index);
  }, [contract.extraChecklist]);

  const updateActionCompletionStatus = (action: IExtraChecklistItem) => {
    if (!exitExtraActions) return undefined;
    dispatch(
      contractActions.updateExtraChecklistItemStatusStart.getAction({
        contractId: contract.id,
        data: {
          key: EPreferences.ChecklistExtraExit,
          actions: exitExtraActions.map((a: IExtraChecklistItem) =>
            a.index === action.index ? { ...a, completed: !a.completed } : a,
          ),
        },
      }),
    );
  };

  const exitExtraChecklistItems: IChecklistItem[] | undefined =
    React.useMemo(() => {
      if (!exitExtraActions) return undefined;
      return exitExtraActions.map(action => ({
        actionStatus: action.completed
          ? EActionStatus.CompletedManually
          : EActionStatus.Incomplete,
        title: action.title,
        content: action.description,
        onClick: () => updateActionCompletionStatus(action),
        onChangeActionStatus: () => updateActionCompletionStatus(action),
      }));
    }, [exitExtraActions, contract.id]);

  const checklistItems = useGetExitChecklistItems({
    contract,
    property,
    rentDeposits,
    isFetchingRentDeposits,
    paymentOrdersCount: hasPaymentV2ForBroker
      ? paymentOrdersV2?.length
      : paymentOrdersV1?.length,
    isFetchingPaymentOrdersCount: hasPaymentV2ForBroker
      ? isFetchingPaymentOrdersV2
      : isFetchingPaymentOrdersV1,

    // Callbacks
    onShowRentDeposit: rentDepositId => {
      setQueryParamValue({
        rentDepositId,
      });
    },
    onStartRelease: rentDepositId =>
      navigate(`/forms/rent-deposit/${rentDepositId}/release`),
    onOrderPropertyInspectionReport: () => {
      setShowLocationDescriptionModal(true);
    },
    onCreateRentioMoveRequest: () => {
      setShowRequestRentioMoveService(true);
    },
    onShowMoveRequest: (moveRequestId: number) => {
      setQueryParamValue({ moveRequestId });
    },
  });

  const exitItems = useGetAdministrationExitItems({
    contract,
    property,
    rentDeposits,
    paymentOrdersV1,
    paymentOrdersV2,
    checklistItems,
    extraChecklistActions: exitExtraActions,
  });

  const handleLocationDescriptionModalClose = () => {
    setShowLocationDescriptionModal(false);
  };

  const handleClickRequestRentioMoveServiceModalClose = () => {
    setShowRequestRentioMoveService(false);
  };

  return (
    <>
      {exitItems?.length && checklistItems && (
        <Card.Section
          boxProps={{
            p: ESpacings.none,
          }}
        >
          <AdministrationItems
            items={exitItems}
            showCircles={showCircles}
            forExitChecklist={true}
            showChecklistItems={showChecklistItems}
            setShowChecklistItems={setShowChecklistItems}
            // checklistItems={compact(Object.values(checklistItems))}
          />
        </Card.Section>
      )}

      {showChecklistItems && checklistItems && (
        <Card.Section
          boxProps={{
            p: ESpacings.none,
            mt: ESpacings.loose,
            pt: ESpacings.tight,
          }}
        >
          <ChecklistItems
            checklistItems={compact(Object.values(checklistItems))}
            property={property}
            contract={contract}
            extraChecklistItems={exitExtraChecklistItems}
          />
        </Card.Section>
      )}

      {showLocationDescriptionModal && (
        <LocationDescriptionModal
          property={property}
          contract={contract}
          onClose={handleLocationDescriptionModalClose}
          type={ELocationDescriptionType.Out}
        />
      )}

      {showRequestRentioMoveService && (
        <EnergyWaterExitModal
          property={property}
          contract={contract}
          contacts={contractContacts}
          onClose={handleClickRequestRentioMoveServiceModalClose}
        />
      )}
    </>
  );
};
